/* eslint-disable no-alert */

import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp, FaRegTrashAlt } from "react-icons/fa";

import { useDeleteSong } from "../../../../api/mutations";
import useSiteConfigAuth from "../../../../api/sanity/queries/getSiteConfigWithCode";
import LoadingSmall from "../../../../components/LoadingSmall";
import RebrandButtonLink from "../../../../components/RebrandButtonLink";
import StatusPill from "../../../../components/StatusPill";
import Table from "../../../../components/Table";
import { sendEvent } from "../../../../helpers/helpers";
// import AppState from "../../../../store/appstate";
import useAppStore from "../../../../store/appStateZustand";
import DeleteWorkModal from "./DeleteWorkModal";
import SongDetails from "./SongDetails";

function StatusCell({ row }) {
  return (
    <div>
      <StatusPill object={row.original} />
    </div>
  );
}

function DeleteCell({ row, deletingWork, handleOpenWarningModal }) {
  const { verifiedstate, changespending, isdeactivated } = row.original;
  const showWarning = localStorage.getItem("showDeleteWorkWarning");
  const isDeleting = row.original.workcode === deletingWork;

  const handleClick = (event) => {
    event.stopPropagation();
    if (showWarning === "false") {
      handleOpenWarningModal(row);
    }
    else {
      handleOpenWarningModal(row);
    }
  };

  return verifiedstate === 0 && changespending && !isdeactivated ? (
    isDeleting ? (
      <LoadingSmall />
    ) : (
      <RebrandButtonLink
        id={`btnSongDelete_${row.original.id}`}
        onClick={handleClick}
        className="hover:scale-125"
      >
        <FaRegTrashAlt size={16} className="inline-block mr-2" />
      </RebrandButtonLink>
    )
  ) : (
    <FaRegTrashAlt
      size={16}
      className="inline-block mr-2 pointer-events-none opacity-20"
    />
  );
}

function createDeleteCellWithProps(deletingWork, handleOpenWarningModal) {
  return props => <DeleteCell {...props} deletingWork={deletingWork} handleOpenWarningModal={handleOpenWarningModal} />;
}

function ExpanderCell({ row }) {
  return <span>{row.isExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>;
}

function SongsTable({ data, highlightColour, total }) {
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [deletingWork, setdeletingWork] = useState(null);
  const isCookingVinyl = useSiteConfigAuth?.display_name?.includes("Cooking Vinyl Publishing");
  const [fadingOutRow, setFadingOutRow] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const { t } = useTranslation();

  // Use the store directly to get the tableState
  const tableState = useAppStore(state => state.songsTable);

  // Define a direct table state update function
  const setTableState = useCallback((type, value) => {
    // Create a manual update to the songsTable state
    useAppStore.setState(state => ({
      songsTable: {
        ...state.songsTable,
        [type]: value,
      },
    }));
  }, []);
  const deleteWork = useDeleteSong();
  const queryClient = useQueryClient();

  const handleWarningClose = () => {
    setIsWarningModalOpen(false);
    setSelectedRow(null);
  };

  const handleDelete = useCallback((rowToDelete) => {
    if (!rowToDelete)
      return;

    const { workcode } = rowToDelete.original;

    setdeletingWork(workcode);
    setFadingOutRow(workcode);

    deleteWork.mutate(
      {
        artistaccountcode: rowToDelete.original.artistaccountcode,
        workcode: rowToDelete.original.workcode,
        isdeleted: true,
      },
      {
        onSuccess: () => {
          console.log(
            `✅ Work ${rowToDelete.original.workcode} deleted successfully.`,
          );

          // First update the table state to remove the deleted song
          setTableState("songs", (prev) => {
            const updatedSongs = prev.filter(song => song.workcode !== rowToDelete.original.workcode);

            // If this was the last song in the filtered results, we need to reset to an empty array
            // to trigger the "no songs" display immediately
            if (updatedSongs.length === 0) {
              // Force a re-render by returning an empty array
              return [];
            }

            return updatedSongs;
          });

          // Invalidate the query to trigger a refetch in the background
          queryClient.invalidateQueries({ queryKey: ["songs"] });

          sendEvent({ property: "pe14489857_user_song_deletion" });

          setTimeout(() => {
            setdeletingWork(null);
            setFadingOutRow(null);
            handleWarningClose();
          }, 700);
        },
        onError: (error) => {
          console.error("❌ Delete failed:", error);
          handleWarningClose();
          alert(`Failed to delete work ${workcode}: ${error.message || error}`);
          setdeletingWork(null);
          setFadingOutRow(null);
        },
      },
    );
  }, [deleteWork, queryClient, setTableState]);

  const handleOpenWarningModal = useCallback((row) => {
    setSelectedRow(row);
    const showWarning = localStorage.getItem("showDeleteWorkWarning");

    if (showWarning === "false") {
      handleDelete(row);
    }
    else {
      setIsWarningModalOpen(true);
    }
  }, [handleDelete]);

  const handleWarningConfirm = () => {
    if (selectedRow) {
      handleDelete(selectedRow);
    }
  };

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        Header: t("songs:song-table-name"),
        accessor: "title",
        width: "20%",
        id: "title",
        Cell: ({ row }) => {
          const song = row.original;
          const title = song.currentamendment && song.currentamendment.title !== song.title
            ? song.currentamendment.title
            : song.title;
          return title;
        },
      },
      {
        Header: t("songs:song-table-performers"),
        accessor: (row) => {
          const performingArtists = row.currentamendment?.performingartists?.length
            ? row.currentamendment.performingartists
            : row.performingartists;

          return performingArtists.filter(x => x.isdeleted === false)
            .map(p => p.performingartistname)
            .join(", ");
        },
        width: "20%",
        disableSortBy: true,
        id: "performer",
      },
      !isCookingVinyl && {
        Header: t("songs:song-table-date"),
        accessor: "createddate",
        width: "20%",
        Cell: ({ row }) =>
          row?.values?.createddate && row?.values?.createddate !== "0000-00-00"
            ? format(Date.parse(row.values.createddate), "yyyy-MM-dd")
            : null,
        id: "createddate",
      },
      {
        Header: t("songs:song-table-writers"),
        accessor: (row) => {
          const writers = row.currentamendment?.writersplits?.length
            ? row.currentamendment.writersplits
            : row.writersplits;

          return writers.filter(x => x.isdeleted === false)
            .map(
              s =>
                `${s?.firstname} ${s?.middlename ? s?.middlename : ""} ${s.lastname}`,
            )
            .join(", ");
        },
        width: "20%",
        disableSortBy: true,
        id: "writers",
      },
      {
        Header: t("songs:song-table-status"),
        accessor: "verifiedstate",
        width: "20%",
        id: "verifiedstate",
        Cell: StatusCell,
      },
      {
        Header: "delete",
        accessor: "delete",
        width: "10%",
        disableSortBy: true,
        id: "deleteSong",
        Cell: createDeleteCellWithProps(deletingWork, handleOpenWarningModal),
      },
      {
        Header: () => null,
        id: "expander",
        width: "5%",
        Cell: ExpanderCell,
        SubCell: () => null,
      },
    ].filter(Boolean);

    return baseColumns;
  }, [t, deletingWork, isCookingVinyl, handleOpenWarningModal]);

  return (
    <>
      <DeleteWorkModal
        isOpen={isWarningModalOpen}
        onConfirm={handleWarningConfirm}
        onClose={handleWarningClose}
      />

      <Table
        area="SongsTable"
        data={Array.isArray(data) ? data : []}
        columns={columns}
        highlightColour={highlightColour}
        revealComponent={<SongDetails />}
        searchSuffix={t("common:songs-search-suffix")}
        paginated
        setTableState={setTableState}
        initialSort={tableState.sortBy}
        onSort={p => setTableState("sortBy", p)}
        tableState={tableState}
        total={typeof total === "number" ? total : 0}
        currentpage={tableState.page}
        setPage={(p) => {
          setTableState("page", p);
        }}
        setFilter={(p) => {
          setTableState("globalFilter", p);
        }}
        filter={tableState.globalFilter}
        setPerPage={p => setTableState("perPage", p)}
        async
        fadingOutRow={fadingOutRow}
      />
    </>
  );
}

export default SongsTable;
